import { AfterViewChecked, Component, ViewEncapsulation } from '@angular/core';
import { DeviceLastActiveTimeframe } from '@cdw-ae/common-data';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { TailwindColors } from '../../../common/constants/tailwind-colors';
import {
  DevicesLastActiveSummary,
  DevicesVersionSummary,
} from '../../../layouts/internal/components/dashboard/dashboard.model';
import { BaseChartAbstract } from '../base-chart.class';
import {
  ChartCustomColor,
  ChartData,
  ChartType,
  VersionInfo,
} from '../models/chart.model';

@Component({
  selector: 'spog-bar-chart',
  templateUrl: './spog-bar-chart.component.html',
  styleUrls: ['./spog-bar-chart.component.scss'],
  imports: [NgxChartsModule],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'flex w-full h-full min-h-[200px]', // change me to change size of gauge chart
  },
})
export class SpogBarChartComponent
  extends BaseChartAbstract
  implements AfterViewChecked
{
  private static readonly PLACEHOLDER_VALUE = 1;
  private static readonly PLACEHOLDER_Y_SCALE_MAX = 100;

  private hasAllZeroValues = false;
  private labelsFixed = false;

  // Format labels: show "0" when displaying placeholder values
  public dataLabelFormatting = (value: number): string => {
    const isPlaceholder =
      this.hasAllZeroValues &&
      value === SpogBarChartComponent.PLACEHOLDER_VALUE;
    return isPlaceholder ? '0' : value.toString();
  };

  // Calculate Y-axis max: use higher scale when showing placeholders to keep bars small
  public get computedYScaleMax(): number | undefined {
    return this.hasAllZeroValues
      ? SpogBarChartComponent.PLACEHOLDER_Y_SCALE_MAX
      : this.yScaleMax;
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  ngAfterViewChecked(): void {
    // Fix label rotation after view updates
    this.fixDataLabelRotation();
  }

  protected override onChartDataChange(): void {
    // Reset flag when data changes to allow re-fixing labels
    this.labelsFixed = false;
  }

  private fixDataLabelRotation(): void {
    if (this.labelsFixed) {
      return;
    }

    const dataLabels = document.querySelectorAll('.textDataLabel');
    if (dataLabels.length === 0) {
      return;
    }

    dataLabels.forEach((element: Element) => {
      const htmlElement = element as HTMLElement;
      htmlElement.style.setProperty('transform', 'none', 'important');
      htmlElement.style.setProperty('text-anchor', 'middle', 'important');
    });

    this.labelsFixed = true;
  }

  protected override getCustomColors(): ChartCustomColor[] {
    if (this.chartType === ChartType.DEVICE_INACTIVITY) {
      return [
        {
          name: DeviceLastActiveTimeframe.SevenDays,
          value: TailwindColors.chartBlue,
        },
        {
          name: DeviceLastActiveTimeframe.FourteenDays,
          value: TailwindColors.chartBlue,
        },
        {
          name: DeviceLastActiveTimeframe.ThirtyDays,
          value: TailwindColors.chartBlue,
        },
      ];
    } else if (this.chartType === ChartType.OS_VERSION_STATE) {
      return [
        {
          name: VersionInfo.LATEST_OS,
          value: TailwindColors.chartGreen,
        },
        {
          name: VersionInfo.ONE_OS_BEHIND,
          value: TailwindColors.chartYellow,
        },
        {
          name: VersionInfo.TWO_OS_BEHIND,
          value: TailwindColors.chartDarkOrange,
        },
        {
          name: VersionInfo.THREE_PLUS_OS_BEHIND,
          value: TailwindColors.chartRed,
        },
      ];
    } else {
      return [];
    }
  }

  protected override initializeChart(
    dataSource: DevicesLastActiveSummary[] | DevicesVersionSummary[]
  ): ChartData[] {
    if (this.chartType === ChartType.OS_VERSION_STATE) {
      const versionSummary = dataSource[0] as DevicesVersionSummary;

      const normalizedSummary: Record<string, number> = {};
      Object.keys(versionSummary).forEach((key) => {
        const value = versionSummary[key as keyof DevicesVersionSummary];
        if (typeof value === 'number') {
          normalizedSummary[key.toLowerCase()] = value;
        }
      });

      const chartData = this.customColors.map(({ name }) => ({
        name,
        value: normalizedSummary[name.toLowerCase()] ?? 0,
      }));

      // Check if all values are 0
      const allZeros = chartData.every((item) => item.value === 0);
      this.hasAllZeroValues = allZeros;

      if (allZeros) {
        // Replace 0 with placeholder value for display purposes
        return chartData.map((item) => ({
          ...item,
          value: SpogBarChartComponent.PLACEHOLDER_VALUE,
        }));
      }

      return chartData;
    } else if (this.chartType === ChartType.DEVICE_INACTIVITY) {
      const lastActiveSummary = dataSource[0] as DevicesLastActiveSummary;
      const lastActiveRecords: Record<string, number> = {
        [DeviceLastActiveTimeframe.SevenDays]:
          lastActiveSummary[DeviceLastActiveTimeframe.SevenDays],
        [DeviceLastActiveTimeframe.FourteenDays]:
          lastActiveSummary[DeviceLastActiveTimeframe.FourteenDays],
        [DeviceLastActiveTimeframe.ThirtyDays]:
          lastActiveSummary[DeviceLastActiveTimeframe.ThirtyDays],
        [DeviceLastActiveTimeframe.Unknown]:
          lastActiveSummary[DeviceLastActiveTimeframe.Unknown],
      };

      const chartData = this.customColors.map(({ name }) => ({
        name,
        value: lastActiveRecords[name] || 0,
      }));

      // Check if all values are 0
      const allZeros = chartData.every((item) => item.value === 0);
      this.hasAllZeroValues = allZeros;

      if (allZeros) {
        // Replace 0 with placeholder value for display purposes
        return chartData.map((item) => ({
          ...item,
          value: SpogBarChartComponent.PLACEHOLDER_VALUE,
        }));
      }

      return chartData;
    } else {
      return [];
    }
  }
}
